import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import clsx from 'clsx';

export interface PhotoGalleryItem {
  url: string;
  caption?: string;
  capturedAt?: string;
  /**
   * Short label burned onto the thumbnail (e.g. "Ref"). Use it for a distinction
   * that must survive being seen at 48px, not for detail — that belongs in the
   * caption, which the lightbox shows.
   */
  tag?: string;
  /** Tone for `tag`. Defaults to a neutral ink chip. */
  tagTone?: 'azure' | 'mint' | 'gold';
}

const TAG_TONE: Record<NonNullable<PhotoGalleryItem['tagTone']>, string> = {
  azure: 'bg-azure text-ink-black',
  mint: 'bg-mint text-ink-black',
  gold: 'bg-gold-bright text-ink-black',
};

interface Props {
  photos: PhotoGalleryItem[];
  label?: string;
  size?: 'thumb' | 'medium' | 'large';
  density?: 'compact' | 'grid' | 'strip';
  className?: string;
  testid?: string;
  emptyText?: string;
  maxVisible?: number;
}

const SIZE_PX: Record<NonNullable<Props['size']>, string> = {
  thumb: 'h-12 w-12',
  medium: 'h-24 w-24',
  large: 'h-40 w-40',
};

export default function PhotoGallery({
  photos,
  label,
  size = 'medium',
  density = 'grid',
  className,
  testid,
  emptyText,
  maxVisible,
}: Props) {
  const [openAt, setOpenAt] = useState<number | null>(null);
  const visible = maxVisible != null ? photos.slice(0, maxVisible) : photos;
  const more = maxVisible != null ? Math.max(0, photos.length - maxVisible) : 0;

  useEffect(() => {
    if (openAt == null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpenAt(null);
      if (e.key === 'ArrowRight') setOpenAt((i) => (i == null ? null : Math.min(photos.length - 1, i + 1)));
      if (e.key === 'ArrowLeft') setOpenAt((i) => (i == null ? null : Math.max(0, i - 1)));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [openAt, photos.length]);

  if (photos.length === 0) {
    if (!emptyText) return null;
    return (
      <div className={clsx('text-cream/40 text-sm italic', className)} data-testid={testid}>
        {emptyText}
      </div>
    );
  }

  return (
    <div className={className} data-testid={testid}>
      {label ? (
        <p className="label-small-caps text-gold-bright mb-2">{label}</p>
      ) : null}
      <div
        className={clsx(
          density === 'grid' && 'grid gap-2 grid-cols-[repeat(auto-fill,minmax(0,max-content))]',
          density === 'compact' && 'flex flex-wrap gap-1.5',
          density === 'strip' && 'flex gap-2 overflow-x-auto',
        )}
      >
        {visible.map((photo, i) => (
          <button
            type="button"
            key={`${photo.url}-${i}`}
            onClick={(e) => {
              e.stopPropagation();
              setOpenAt(i);
            }}
            className={clsx(
              'group relative shrink-0 overflow-hidden rounded-md border border-gold-dim/30 bg-ink-black/50',
              'hover:border-gold-bright/80 hover:shadow-gold-glow transition',
              SIZE_PX[size],
            )}
            data-testid={testid ? `${testid}-thumb-${i}` : undefined}
            aria-label={photo.caption ?? 'View photo'}
          >
            <img
              src={photo.url}
              alt={photo.caption ?? 'reference'}
              loading="lazy"
              className="h-full w-full object-cover"
            />
            {photo.tag ? (
              <span
                className={clsx(
                  'absolute inset-x-0 bottom-0 truncate px-0.5 font-inter text-[8px] uppercase tracking-wide',
                  TAG_TONE[photo.tagTone ?? 'gold'],
                )}
                data-testid={testid ? `${testid}-tag-${i}` : undefined}
              >
                {photo.tag}
              </span>
            ) : null}
          </button>
        ))}
        {more > 0 ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setOpenAt(maxVisible ?? 0);
            }}
            className={clsx(
              'shrink-0 rounded-md border border-gold-dim/30 bg-ink-black/50 text-cream/70',
              'flex items-center justify-center text-xs font-medium hover:border-gold-bright/80',
              SIZE_PX[size],
            )}
            data-testid={testid ? `${testid}-more` : undefined}
          >
            +{more}
          </button>
        ) : null}
      </div>

      <AnimatePresence>
        {openAt != null ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-overlay/90 backdrop-blur-sm px-4"
            onClick={() => setOpenAt(null)}
            data-testid="photo-lightbox"
          >
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setOpenAt(null);
              }}
              className="absolute top-4 right-4 btn-quiet"
              aria-label="Close"
              data-testid="photo-lightbox-close"
            >
              <X size={16} />
            </button>
            {openAt > 0 ? (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenAt(openAt - 1);
                }}
                className="absolute left-6 btn-quiet"
                aria-label="Previous"
              >
                <ChevronLeft size={18} />
              </button>
            ) : null}
            {openAt < photos.length - 1 ? (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenAt(openAt + 1);
                }}
                className="absolute right-6 btn-quiet"
                aria-label="Next"
              >
                <ChevronRight size={18} />
              </button>
            ) : null}
            <motion.figure
              key={openAt}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.96 }}
              className="max-h-[88vh] max-w-[88vw]"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={photos[openAt]!.url}
                alt={photos[openAt]!.caption ?? 'reference'}
                className="max-h-[80vh] max-w-[88vw] rounded-md shadow-gold-glow object-contain"
              />
              {photos[openAt]!.caption ? (
                <figcaption className="text-cream/80 text-sm mt-2 text-center">
                  {photos[openAt]!.caption}
                </figcaption>
              ) : null}
            </motion.figure>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
}
